<template>
    <!-- 새 벤더코드 참여 요청 (#442) — /reg/join 요청을 레벨 4 가 승인(회사+벤더코드 생성)·반려한다 -->
    <b-container fluid>
        <div class="border-bottom mb-3 pb-3">
            <h3>벤더코드 참여 요청</h3>
            <p class="fw-400 text-secondary mb-0">
                업체코드가 없는 업체의 참여 요청을 확인합니다. 승인하면 회사와 벤더코드가 만들어지고, 요청자에게 신청 링크가 메일로 갑니다.
            </p>
        </div>

        <b-card no-body>
            <template #header>
                <div class="d-flex align-items-center justify-content-between">
                    <b-form-radio-group v-model="status" :options="statusOptions" buttons button-variant="outline-primary"
                                        size="sm" :disabled="loading" @change="load"/>
                    <b-spinner v-if="loading" small variant="primary"/>
                </div>
            </template>
            <b-table :items="rows" :fields="fields" show-empty empty-text="요청이 없습니다." small hover responsive class="mb-0">
                <template #cell(created_at)="{ value }">{{ formatDate(value) }}</template>
                <template #cell(applicant)="{ item }">
                    <div>{{ item.applicant_name }}</div>
                    <div class="small text-secondary">{{ item.applicant_email }}</div>
                </template>
                <template #cell(status)="{ value }">
                    <b-badge :variant="statusVariant[value]">{{ statusLabel[value] }}</b-badge>
                </template>
                <template #cell(actions)="{ item }">
                    <b-btn size="sm" variant="outline-primary" @click="openDetail(item.id)">보기</b-btn>
                </template>
            </b-table>
        </b-card>

        <b-modal v-model="detailOpen" :title="detail ? `${detail.company_name} 참여 요청` : '참여 요청'" size="lg" hide-footer>
            <div v-if="detail">
                <b-table-simple small bordered class="mb-3">
                    <b-tbody>
                        <b-tr><b-th class="w-25">프로젝트 코드</b-th><b-td>{{ detail.project_code }}</b-td></b-tr>
                        <b-tr><b-th>기업명</b-th><b-td>{{ detail.company_name }}</b-td></b-tr>
                        <b-tr><b-th>사업자등록번호</b-th><b-td>{{ detail.business_no }}</b-td></b-tr>
                        <b-tr><b-th>국가</b-th><b-td>{{ detail.country || '-' }}</b-td></b-tr>
                        <b-tr><b-th>담당자</b-th><b-td>{{ detail.applicant_name }} / {{ detail.applicant_email }} / {{ detail.applicant_tel || '-' }}</b-td></b-tr>
                        <b-tr><b-th>요청 사항</b-th><b-td class="text-pre-wrap">{{ detail.memo || '-' }}</b-td></b-tr>
                        <b-tr><b-th>요청 화면 / 언어</b-th><b-td>{{ detail.origin || '-' }} / {{ detail.lang }}</b-td></b-tr>
                        <b-tr><b-th>요청일</b-th><b-td>{{ formatDate(detail.created_at) }}</b-td></b-tr>
                        <b-tr><b-th>상태</b-th><b-td><b-badge :variant="statusVariant[detail.status]">{{ statusLabel[detail.status] }}</b-badge></b-td></b-tr>
                        <b-tr v-if="detail.status === 'approved'"><b-th>연결 회사 / 벤더코드</b-th><b-td>{{ detail.company_id }} / {{ detail.vendor_code }}</b-td></b-tr>
                        <b-tr v-if="detail.status === 'rejected'"><b-th>반려 사유</b-th><b-td class="text-pre-wrap">{{ detail.reject_reason }}</b-td></b-tr>
                    </b-tbody>
                </b-table-simple>

                <template v-if="detail.status === 'pending'">
                    <h6 class="fw-600">회사 연결</h6>
                    <b-form-radio-group v-model="approve.company_id" stacked class="mb-3">
                        <b-form-radio :value="null">새 회사로 만들기 — {{ detail.company_name }} ({{ detail.business_no }})</b-form-radio>
                        <b-form-radio v-for="c in detail.candidates" :key="c.id" :value="c.id" :disabled="c.has_seat_in_project">
                            기존 회사 #{{ c.id }} {{ c.company_name }} ({{ c.company_code || '번호 없음' }}{{ c.country ? `, ${c.country}` : '' }})
                            <span v-if="c.has_seat_in_project" class="text-danger small"> — 이 프로젝트에 이미 벤더코드 있음</span>
                        </b-form-radio>
                    </b-form-radio-group>
                    <p v-if="!detail.candidates.length" class="small text-secondary mt-n2">같은 사업자등록번호·기업명의 기존 회사가 없습니다.</p>

                    <b-form-group label="벤더코드" label-for="join-vendor-code" description="자동 발급값입니다. 필요하면 바꿔 입력하세요.">
                        <b-form-input id="join-vendor-code" v-model="approve.vendor_code" maxlength="45" :disabled="acting"/>
                    </b-form-group>
                    <div class="text-right mb-4">
                        <b-btn variant="primary" :disabled="acting || !approve.vendor_code.trim()" @click="doApprove">
                            <b-spinner v-if="acting" small class="mr-1"/>승인
                        </b-btn>
                    </div>

                    <h6 class="fw-600">반려</h6>
                    <b-textarea v-model="rejectReason" rows="2" placeholder="반려 사유 (요청자에게 메일로 전달됩니다)" class="mb-2" :disabled="acting"/>
                    <div class="text-right">
                        <b-btn variant="outline-danger" :disabled="acting || !rejectReason.trim()" @click="doReject">반려</b-btn>
                    </div>
                </template>

                <b-alert :show="!!actionMessage" :variant="actionVariant" class="mt-3 mb-0 text-break text-pre-wrap">{{ actionMessage }}</b-alert>
            </div>
            <div v-else class="text-center py-4"><b-spinner variant="primary"/></div>
        </b-modal>
    </b-container>
</template>

<script>
const ERRORS = {
    Not_Pending_Request: '이미 처리된 요청입니다.',
    Duplicate_Company_Name: '같은 기업명의 회사가 이미 있습니다. 기존 회사를 선택해주세요.',
    Already_Registered_Vendor: '선택한 회사는 이 프로젝트에 이미 벤더코드가 있습니다.',
    Duplicate_Vendor_Code: '이 프로젝트에 같은 벤더코드가 이미 있습니다. 다른 값을 입력해주세요.',
    Project_Has_No_Period: '프로젝트에 기간(period)이 없어 새 회사를 만들 수 없습니다.',
    Not_Found_Project: '프로젝트를 찾을 수 없습니다.',
    Not_Found_Company: '선택한 회사를 찾을 수 없습니다.',
};

export default {
    name: 'VendorJoinRequestManagement',
    data() {
        return {
            status: 'pending',
            statusOptions: [
                {text: '대기', value: 'pending'}, {text: '승인', value: 'approved'},
                {text: '반려', value: 'rejected'}, {text: '전체', value: null},
            ],
            statusLabel: {pending: '대기', approved: '승인', rejected: '반려'},
            statusVariant: {pending: 'warning', approved: 'success', rejected: 'secondary'},
            fields: [
                {key: 'created_at', label: '요청일'},
                {key: 'project_code', label: '프로젝트 코드'},
                {key: 'company_name', label: '기업명'},
                {key: 'business_no', label: '사업자등록번호'},
                {key: 'applicant', label: '담당자'},
                {key: 'status', label: '상태'},
                {key: 'actions', label: ''},
            ],
            rows: [],
            loading: false,
            detailOpen: false,
            detail: null,
            approve: {company_id: null, vendor_code: ''},
            rejectReason: '',
            acting: false,
            actionMessage: null,
            actionVariant: 'success',
        };
    },
    mounted() {
        this.load();
    },
    methods: {
        formatDate(value) {
            return value ? value.replace('T', ' ').slice(0, 16) : '-';
        },
        async load() {
            this.loading = true;
            try {
                const {data} = await this.$axios.get('/vendor-join-requests', {params: this.status ? {status: this.status} : {}});
                this.rows = data;
            } catch (e) {
                this.rows = [];
                alert('참여 요청 목록을 불러오지 못했습니다.');
            } finally {
                this.loading = false;
            }
        },
        async openDetail(id) {
            this.detail = null;
            this.actionMessage = null;
            this.rejectReason = '';
            this.detailOpen = true;
            try {
                const {data} = await this.$axios.get(`/vendor-join-requests/${id}`);
                this.detail = data;
                this.approve = {company_id: null, vendor_code: data.suggested_vendor_code || ''};
            } catch (e) {
                this.detailOpen = false;
                alert('참여 요청을 불러오지 못했습니다.');
            }
        },
        showResult(message, variant = 'success') {
            this.actionMessage = message;
            this.actionVariant = variant;
        },
        errorMessage(e) {
            const message = e?.response?.data?.message;
            return ERRORS[message] || `처리하지 못했습니다. (${message || e?.message})`;
        },
        async doApprove() {
            const target = this.approve.company_id ? `기존 회사 #${this.approve.company_id}` : '새 회사';
            if (!confirm(`${target} 에 벤더코드 ${this.approve.vendor_code.trim()} 를 만들고 승인합니다. 진행할까요?`)) return;
            this.acting = true;
            try {
                const {data} = await this.$axios.post(`/vendor-join-requests/${this.detail.id}/approve`, {
                    company_id: this.approve.company_id,
                    vendor_code: this.approve.vendor_code.trim(),
                });
                const mail = data.mail_sent ? '요청자에게 신청 링크 메일을 보냈습니다.' : '메일 발송에 실패했습니다. 아래 링크를 요청자에게 직접 전달해주세요.';
                await this.reloadDetail();
                this.showResult(`승인 완료 — 벤더코드 ${data.vendor_code}. ${mail}\n${data.invite_url}`, data.mail_sent ? 'success' : 'warning');
            } catch (e) {
                this.showResult(this.errorMessage(e), 'danger');
            } finally {
                this.acting = false;
            }
        },
        async doReject() {
            if (!confirm('이 요청을 반려합니다. 같은 프로젝트·사업자등록번호로는 다시 요청할 수 없습니다. 진행할까요?')) return;
            this.acting = true;
            try {
                const {data} = await this.$axios.post(`/vendor-join-requests/${this.detail.id}/reject`, {reason: this.rejectReason.trim()});
                await this.reloadDetail();
                this.showResult(data.mail_sent ? '반려했습니다. 요청자에게 메일을 보냈습니다.' : '반려했습니다. 메일 발송에 실패했습니다.',
                    data.mail_sent ? 'success' : 'warning');
            } catch (e) {
                this.showResult(this.errorMessage(e), 'danger');
            } finally {
                this.acting = false;
            }
        },
        async reloadDetail() {
            const {data} = await this.$axios.get(`/vendor-join-requests/${this.detail.id}`);
            this.detail = data;
            this.load();
        },
    },
};
</script>

<style scoped>
.text-pre-wrap {
    white-space: pre-wrap;
}
</style>
